import { USER_NAME, VERCEL_SERVER_URL } from "../config/constants"
import { getPassword } from "../modules/auth"

export interface ChallengeData {
  created_by: string
  hard_mode: boolean
  hints_used: number
  id: number
  index: number
  // The creator's `result_created_by`; `null` until they submit their time.
  time: null | number
}

export interface ChallengeResult {
  challenge: ChallengeData | null
  fromMock: boolean
}

export type ChallengeStatus = "lost" | "pending" | "won"

export interface CompleteChallengeBody {
  accepted_challenge_time: null | number
  hints_used: number
  id: number
  winner: string
}

export interface CreateCompletionBody {
  hints_used: number
  id: number
  time: number
}

// Global leaderboard row (GET /sortaballs/stats).
export interface StatRow {
  avg_hints: number
  current_streak: number
  easy_played_pct: number
  easy_won_pct: number
  hard_played_pct: number
  hard_won_pct: number
  id: number
  losses: number
  name: string
  total_diff: number
  total_games: number
  win_pct: number
  wins: number
}

// A challenge the user participated in (GET /sortaballs/user/challenges and
// /sortaballs/user/pending). `result.*` times are seconds.
export interface UserChallengeRow {
  acceptor_time: null | number
  completed_at: null | string
  created_at: string
  creator_name: null | string
  creator_time: null | number
  hard_mode: boolean
  id: number
  opponent_name: null | string
  status: ChallengeStatus
}

export interface UserChallengesResponse {
  challenges: UserChallengeRow[]
  server_time: string
}

// The challenge endpoints are not deployed yet, so requests are expected to
// fail — fall back to mock data and keep the mode playable.
const MOCK_ACCEPT_CHALLENGE: ChallengeData = {
  created_by: "mock-opponent",
  hard_mode: true,
  hints_used: 5,
  id: 1,
  index: 4,
  time: 20,
}

const MOCK_CREATE_ID = 123
const MOCK_CREATE_LEVEL = 23

// A challenge with no opponent yet: only id + level + mode are meaningful.
const toChallengeData = (input: {
  hard_mode: boolean
  id: number
  level: number
}): ChallengeData=> ({
  created_by: "",
  hard_mode: input.hard_mode,
  hints_used: 0,
  id: input.id,
  index: input.level,
  time: 0,
})

// An empty body (e.g. a request with no opponent yet) is a valid "no
// challenge" answer, not a failure — distinguish it from the mock path.
// eslint-disable anti-slop/no-runtime-typeof -- boundary type guard
export function acceptChallenge(): Promise<ChallengeResult> {
  return requestChallenge("/sortaballs/accept-challenge")
}
// eslint-enable anti-slop/no-runtime-typeof

// eslint-disable anti-slop/no-runtime-typeof -- boundary type guard
export function completeChallenge(body: CompleteChallengeBody): void {
  reportCompletion(body)
}
// eslint-enable anti-slop/no-runtime-typeof

export function completeCreateChallenge(body: CreateCompletionBody): void {
  reportCompletion(body)
}

export async function createChallenge(hardMode: boolean): Promise<ChallengeResult> {
  const name = localStorage.getItem(USER_NAME) ?? ""
  try {
    const response = await fetch(`${VERCEL_SERVER_URL}/sortaballs/create-challenge`, {
      body: JSON.stringify({ hard_mode: hardMode, name, password: getPassword() ?? "" }),
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
    })
    if (response.ok === false) {
      throw new Error(`create-challenge failed with ${response.status}`)
    }
    const payload: unknown = await response.json()
    if (isCreateChallengeData(payload)) {
      return { challenge: toChallengeData(payload), fromMock: false }
    }
    return { challenge: null, fromMock: false }
  } catch {
    return {
      challenge: toChallengeData({
        hard_mode: hardMode,
        id: MOCK_CREATE_ID,
        level: MOCK_CREATE_LEVEL,
      }),
      fromMock: true,
    }
  }
}

// Count of challenges the user can still accept (created by others, unaccepted,
// no winner). Dedicated count endpoint returns `{ count }`.
export function getAvailableChallengesCount(name: string): Promise<number> {
  return getQuery<{ count: number }>(
    `/sortaballs/available-challenges-count?name=${encodeURIComponent(name)}`,
  ).then(body=> body.count)
}

// Count of challenges the user created but no opponent has accepted yet.
export function getMyPendingCount(name: string, hardMode: boolean): Promise<number> {
  return getQuery<{ count: number }>(
    `/sortaballs/my-pending-count?name=${encodeURIComponent(name)}&hard_mode=${hardMode}`,
  ).then(body=> body.count)
}

export function getStats(): Promise<StatRow[]> {
  return getQuery<StatRow[]>("/sortaballs/stats")
}

export function getUserChallenges(name: string): Promise<UserChallengesResponse> {
  return getQuery<UserChallengesResponse>(
    `/sortaballs/user/challenges?name=${encodeURIComponent(name)}`,
  )
}

// Read-only query routes. Auth is the API password in the query string; the
// user is identified by `name` only (the server resolves it to an id).
async function getQuery<T>(path: string): Promise<T> {
  const password = getPassword() ?? ""
  // Paths may already carry a `?name=…` query; merge with `&` instead of a
  // second `?`, otherwise the password lands inside the name value and auth
  // fails.
  const separator = path.includes("?") ? "&" : "?"
  const response = await fetch(
    `${VERCEL_SERVER_URL}${path}${separator}password=${encodeURIComponent(password)}`,
    { method: "GET" },
  )
  if (response.ok === false) {
    throw new Error(`sortaballs query ${path} failed with ${response.status}`)
  }
  // SAFETY: API response matches generic type T
  return (await response.json()) as T
}

function isChallengeData(json: unknown): json is ChallengeData {
  if (typeof json !== "object" || json === null) {
    return false
  }
  // SAFETY: boundary type guard, narrowing unknown to Record
  const candidate = json as Record<string, boolean | null | number | string>
  return (
    typeof candidate.created_by === "string" &&
    typeof candidate.hard_mode === "boolean" &&
    typeof candidate.hints_used === "number" &&
    typeof candidate.id === "number" &&
    typeof candidate.index === "number" &&
    (candidate.time === null || typeof candidate.time === "number")
  )
}

function isCreateChallengeData(
  json: unknown,
): json is { hard_mode: boolean; id: number; level: number } {
  if (typeof json !== "object" || json === null) {
    return false
  }
  // SAFETY: boundary type guard, narrowing unknown to Record
  const candidate = json as Record<string, boolean | null | number | string>
  return (
    typeof candidate.hard_mode === "boolean" &&
    typeof candidate.id === "number" &&
    typeof candidate.level === "number"
  )
}

// Reports a battle result. Intentionally fire-and-forget: per plan, a failed
// report is silently ignored (endpoint not deployed yet).
function reportCompletion(body: CompleteChallengeBody | CreateCompletionBody): void {
  fetch(`${VERCEL_SERVER_URL}/sortaballs/complete-challenge`, {
    body: JSON.stringify({ ...body, password: getPassword() ?? "" }),
    headers: {
      "Content-Type": "application/json",
    },
    method: "POST",
  })
    .then(response=> {
      if (response.ok === false) {
        throw new Error(`complete-challenge failed with ${response.status}`)
      }
    })
    .catch(()=> undefined)
}

async function requestChallenge(endpoint: string): Promise<ChallengeResult> {
  const name = localStorage.getItem(USER_NAME) ?? ""
  try {
    const response = await fetch(`${VERCEL_SERVER_URL}${endpoint}`, {
      body: JSON.stringify({ name, password: getPassword() ?? "" }),
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
    })
    if (response.ok === false) {
      throw new Error(`challenge request failed with ${response.status}`)
    }
    const payload: unknown = await response.json()
    if (isChallengeData(payload)) {
      return { challenge: payload, fromMock: false }
    }
    return { challenge: null, fromMock: false }
  } catch {
    return { challenge: MOCK_ACCEPT_CHALLENGE, fromMock: true }
  }
}